
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useLinkedInPosts } from '@/hooks/useLinkedInPosts';
import { useToast } from '@/hooks/use-toast';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { CalendarIcon, ArrowLeft, Save, Calendar as CalendarIcon2, Image, Video, X, FileVideo } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LinkedInPostFormData, LinkedInPostType, LinkedInPostStatus, LinkedInPost, LinkedInMedia } from '@/types/linkedin';
import { MockBadge } from '@/components/ui/mock-badge';
import { useForm } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { UnsplashImagePickerModal } from '@/components/blog-management/UnsplashImagePickerModal';
import { FileUploadInput } from '@/components/linkedin/FileUploadInput';
import { VideoPreview } from '@/components/linkedin/VideoPreview';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

const LinkedInPostEditor = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { posts, loading, createPost, updatePost } = useLinkedInPosts();
  const [post, setPost] = useState<LinkedInPost | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaType, setMediaType] = useState<'image' | 'video'>('image');
  const [mediaUploadTab, setMediaUploadTab] = useState<'url' | 'upload'>('url');
  
  // Determine the return path after saving
  const getReferrer = () => {
    // Check if there's a state with referrer information
    const state = location.state as { referrer?: string } | undefined;
    if (state?.referrer) {
      return state.referrer;
    }
    
    // Default to LinkedIn management page if no referrer is specified
    return '/dashboard/linkedin';
  };

  // Initialize form with default values
  const form = useForm<LinkedInPostFormData>({
    defaultValues: {
      content_text: '',
      post_type: 'text',
      status: 'draft',
      scheduled_at: null,
      media_url: []
    }
  });

  // Set form values when post data is loaded
  useEffect(() => {
    if (id && posts.length > 0 && !post) {
      const foundPost = posts.find(p => p.id === id);
      if (foundPost) {
        setPost(foundPost);
        form.reset({
          content_text: foundPost.content_text,
          post_type: foundPost.post_type,
          status: foundPost.status,
          scheduled_at: foundPost.scheduled_at ? new Date(foundPost.scheduled_at) : null,
          media_url: Array.isArray(foundPost.media_url) ? foundPost.media_url : []
        });
      }
    }
  }, [id, posts, post, form]);

  const handleSubmit = async (formData: LinkedInPostFormData) => {
    setIsSaving(true);
    try {
      if (id && post) {
        // Update existing post
        const success = await updatePost(id, formData);
        if (success) {
          toast({
            title: "Post updated",
            description: "Your LinkedIn post has been updated successfully."
          });
          // Navigate back to the referrer page
          navigate(getReferrer());
        }
      } else {
        // Create new post
        const newPost = await createPost(formData);
        if (newPost) {
          toast({
            title: "Post created",
            description: "Your LinkedIn post has been created successfully."
          });
          // Navigate back to the referrer page
          navigate(getReferrer());
        }
      }
    } catch (error) {
      console.error("Error saving post:", error);
      toast({
        title: "Error",
        description: "There was a problem saving your post.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle adding a new media item from URL input
  const handleAddMediaUrl = () => {
    if (!mediaUrl.trim()) return;
    
    const newMedia: LinkedInMedia = {
      url: mediaUrl,
      type: mediaType
    };
    
    const currentMedia = form.getValues().media_url || [];
    form.setValue('media_url', [...currentMedia, newMedia]);
    
    // Update post type based on media
    if (mediaType === 'image') {
      form.setValue('post_type', 'image');
    } else if (mediaType === 'video') {
      form.setValue('post_type', 'video');
    }
    
    // Reset the input field
    setMediaUrl('');
  };

  // Handle removing a media item
  const handleRemoveMedia = (index: number) => {
    const currentMedia = [...(form.getValues().media_url || [])];
    currentMedia.splice(index, 1);
    form.setValue('media_url', currentMedia);
    
    // If no media left, revert to text post type
    if (currentMedia.length === 0) {
      form.setValue('post_type', 'text');
    }
  };

  // Handle file upload
  const handleFileUploaded = (url: string, type: 'image' | 'video', file: File) => {
    const newMedia: LinkedInMedia = {
      url: url,
      type: type,
      id: `upload-${Date.now()}`
    };
    
    const currentMedia = form.getValues().media_url || [];
    form.setValue('media_url', [...currentMedia, newMedia]);
    
    // Update post type based on media
    form.setValue('post_type', type);
    
    toast({
      title: "Media added",
      description: `Your ${type} has been added to the post.`
    });
  };

  // Handle selecting an image from Unsplash
  const handleUnsplashImageSelect = (imageData: {
    url: string;
    description: string;
    attribution: {
      photographerName: string;
      photographerUsername: string;
      photographerUrl: string;
      unsplashUrl: string;
    };
    width: number;
    height: number;
  }) => {
    const newMedia: LinkedInMedia = {
      url: imageData.url,
      type: 'image',
      id: `unsplash-${Date.now()}`
    };
    
    const currentMedia = form.getValues().media_url || [];
    form.setValue('media_url', [...currentMedia, newMedia]);
    form.setValue('post_type', 'image');
  };

  const pageTitle = id ? 'Edit LinkedIn Post' : 'Create LinkedIn Post';
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(getReferrer())}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">{pageTitle}</h1>
        </div>
        <MockBadge feature="LinkedIn Post Editor" phase="2" />
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Post Content</CardTitle>
                <CardDescription>
                  Write your LinkedIn post content and choose post type
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="content_text"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Content</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Write your LinkedIn post content..."
                          className="min-h-[200px]"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Write engaging content for your LinkedIn audience
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="post_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Post Type</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={(value) => field.onChange(value as LinkedInPostType)}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select post type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="text">Text Only</SelectItem>
                          <SelectItem value="image">Image</SelectItem>
                          <SelectItem value="video">Video</SelectItem>
                          <SelectItem value="article">Article</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        The type of content you want to post
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Media Card */}
            <Card>
              <CardHeader>
                <CardTitle>Media</CardTitle>
                <CardDescription>
                  Add images or videos to your LinkedIn post
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Display current media */}
                {form.watch('media_url')?.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium">Added Media</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {form.watch('media_url').map((media, index) => (
                        <div key={index} className="relative border rounded-md overflow-hidden">
                          {media.type === 'image' ? (
                            <img 
                              src={media.url} 
                              alt={`Media ${index + 1}`} 
                              className="w-full h-40 object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = '/placeholder.svg';
                              }}
                            />
                          ) : (
                            <VideoPreview
                              url={media.url}
                              onRemove={() => handleRemoveMedia(index)}
                              disabled={isSaving}
                            />
                          )}
                          {media.type === 'image' && (
                            <Button
                              variant="destructive"
                              size="icon"
                              className="absolute top-2 right-2"
                              onClick={() => handleRemoveMedia(index)}
                              type="button"
                              disabled={isSaving}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Media input options */}
                <div className="space-y-4">
                  <Tabs value={mediaUploadTab} onValueChange={(v) => setMediaUploadTab(v as 'url' | 'upload')} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 mb-4">
                      <TabsTrigger value="url">URL Input</TabsTrigger>
                      <TabsTrigger value="upload">File Upload</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="url" className="space-y-4">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <div className="flex-1">
                          <Input
                            placeholder="Enter media URL..."
                            value={mediaUrl}
                            onChange={(e) => setMediaUrl(e.target.value)}
                            disabled={isSaving}
                          />
                        </div>
                        <Select
                          value={mediaType}
                          onValueChange={(value) => setMediaType(value as 'image' | 'video')}
                          disabled={isSaving}
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue placeholder="Media type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="image">
                              <div className="flex items-center">
                                <Image className="mr-2 h-4 w-4" />
                                <span>Image</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="video">
                              <div className="flex items-center">
                                <Video className="mr-2 h-4 w-4" />
                                <span>Video</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <Button 
                          type="button"
                          onClick={handleAddMediaUrl}
                          disabled={!mediaUrl.trim() || isSaving}
                        >
                          Add
                        </Button>
                      </div>

                      <div className="flex gap-2 items-center mt-2">
                        <div className="text-sm">Or</div>
                        <UnsplashImagePickerModal 
                          onImageSelect={handleUnsplashImageSelect}
                          buttonLabel="Choose from Unsplash"
                        />
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="upload" className="space-y-4">
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FileVideo className="h-5 w-5 text-primary" />
                          <h3 className="text-sm font-medium">Upload Video</h3>
                        </div>
                        <FileUploadInput 
                          mediaType="video"
                          onFileUploaded={handleFileUploaded}
                          disabled={isSaving}
                        />
                        <p className="text-xs text-muted-foreground">
                          Supported formats: MP4, WebM, MOV, AVI. Maximum size: 100MB.
                        </p>
                      </div>
                      
                      <div className="border-t pt-4 space-y-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Image className="h-5 w-5 text-primary" />
                          <h3 className="text-sm font-medium">Upload Image</h3>
                        </div>
                        <FileUploadInput 
                          mediaType="image"
                          onFileUploaded={handleFileUploaded}
                          disabled={isSaving}
                        />
                        <p className="text-xs text-muted-foreground">
                          Supported formats: JPG, PNG, GIF, WebP. Maximum size: 5MB.
                        </p>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Publishing Options</CardTitle>
                <CardDescription>
                  Configure when and how your post will be published
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value as LinkedInPostStatus);
                          // Reset scheduled date if status is not 'scheduled'
                          if (value !== 'scheduled') {
                            form.setValue('scheduled_at', null);
                          }
                        }}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="scheduled">Scheduled</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Select whether to save as draft or schedule for publishing
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {form.watch('status') === 'scheduled' && (
                  <FormField
                    control={form.control}
                    name="scheduled_at"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Scheduled Date</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant={"outline"}
                                className={cn(
                                  "w-full pl-3 text-left font-normal",
                                  !field.value && "text-muted-foreground"
                                )}
                              >
                                {field.value ? (
                                  format(field.value, "PPP")
                                ) : (
                                  <span>Pick a date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value || undefined}
                              onSelect={field.onChange}
                              initialFocus
                              disabled={(date) => date < new Date()}
                            />
                          </PopoverContent>
                        </Popover>
                        <FormDescription>
                          Choose when to publish your LinkedIn post
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard/linkedin')}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={isSaving}
                  className="flex items-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <span className="animate-spin">
                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </span>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>Save Post</span>
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default LinkedInPostEditor;
